// orologio.ts — sincronizzazione dell'orologio del telefono con quello del server.
//
// Serve al conto alla rovescia: il server dice "VIA alle 12:00:03.500 (ora del server)" e ogni
// telefono lo converte nella propria ora. Metodo tipo NTP: per ogni scambio ping/pong
//   t0 = ora del telefono all'invio, t1 = ora del server alla risposta, t2 = ora del telefono all'arrivo
//   ritardo (andata e ritorno) = t2 - t0
//   scarto stimato = t1 - (t0 + t2) / 2      (ora_server ≈ ora_telefono + scarto)
// Si tengono gli ultimi campioni e si usa quello con il ritardo più basso: è il più affidabile.

export interface CampioneOrologio {
  t0: number; // ms, orologio del telefono all'invio
  t1: number; // ms, orologio del server
  t2: number; // ms, orologio del telefono alla risposta
}

export interface StimaOrologio {
  scartoMs: number; // da sommare all'ora del telefono per ottenere quella del server
  ritardoMs: number; // andata e ritorno del campione scelto
  campioni: number;
}

/** Calcola scarto e ritardo di un singolo campione; null se il campione è impossibile. */
export function misuraCampione(c: CampioneOrologio): { scartoMs: number; ritardoMs: number } | null {
  const ritardo = c.t2 - c.t0;
  if (!Number.isFinite(ritardo) || ritardo < 0 || !Number.isFinite(c.t1)) return null;
  return { scartoMs: c.t1 - (c.t0 + c.t2) / 2, ritardoMs: ritardo };
}

/** Sceglie la stima migliore (ritardo minimo) tra i campioni; null se nessuno è valido. */
export function stimaOrologio(campioni: readonly CampioneOrologio[]): StimaOrologio | null {
  let migliore: { scartoMs: number; ritardoMs: number } | null = null;
  let validi = 0;
  for (const c of campioni) {
    const m = misuraCampione(c);
    if (!m) continue;
    validi++;
    if (!migliore || m.ritardoMs < migliore.ritardoMs) migliore = m;
  }
  if (!migliore) return null;
  return { scartoMs: Math.round(migliore.scartoMs), ritardoMs: Math.round(migliore.ritardoMs), campioni: validi };
}

/** Converte un istante del server nell'ora del telefono. */
export function oraServerInOraLocale(oraServerMs: number, stima: StimaOrologio): number {
  return oraServerMs - stima.scartoMs;
}

/** Tiene al massimo `massimo` campioni, i più recenti. */
export function aggiungiCampione(
  campioni: readonly CampioneOrologio[],
  nuovo: CampioneOrologio,
  massimo: number,
): CampioneOrologio[] {
  const tutti = [...campioni, nuovo];
  return tutti.slice(Math.max(0, tutti.length - massimo));
}
